<template>
  <AeModal :model-value="invitation !== null" :title="t('invitationModal.title')" @update:model-value="$emit('close')">
    <div v-if="invitation" class="inv-modal">

      <!-- Step 1: dettagli invito -->
      <template v-if="step === 'details'">
        <div class="inv-org">
          <strong>{{ invitation.target_name }}</strong>
        </div>
        <p v-if="invitation.inviter_name" class="inv-from">
          {{ t('invitationModal.invitedBy') }} <strong>{{ invitation.inviter_name }}</strong>
        </p>
        <p v-if="invitation.invitation_type === 'org'" class="inv-desc">
          {{ t('invitationModal.acceptOrgDesc') }}
        </p>
        <div v-if="error" class="inv-error">{{ error }}</div>
        <div class="inv-actions">
          <AeButton variant="outline" @click="handleReject" :disabled="loading">{{ t('invitationModal.reject') }}</AeButton>
          <AeButton @click="handleAccept" :disabled="loading">
            {{ loading ? t('invitationModal.waiting') : t('invitationModal.accept') }}
          </AeButton>
        </div>
      </template>

      <!-- Step 2: avviso nuovo profilo (Caso 3) -->
      <template v-else-if="step === 'new-profile'">
        <div class="inv-new-profile-icon">👤+</div>
        <p>
          {{ t('invitationModal.alreadyHaveProfile') }}<br>
          {{ t('invitationModal.newProfileWillBeCreatedFor') }}
          <strong>{{ invitation.target_name }}</strong>.
        </p>
        <p class="inv-hint">
          {{ t('invitationModal.switchProfileHint') }}
        </p>
        <div class="inv-actions">
          <AeButton variant="outline" @click="$emit('close')">{{ t('invitationModal.cancel') }}</AeButton>
          <AeButton @click="confirmNewProfile" :disabled="loading">
            {{ loading ? t('invitationModal.creating') : t('invitationModal.createNewProfile') }}
          </AeButton>
        </div>
      </template>

      <!-- Step 3: successo -->
      <template v-else-if="step === 'done'">
        <div class="inv-done-icon">✓</div>
        <p v-if="newProfileCreated">
          {{ t('invitationModal.newProfileCreatedFor') }} <strong>{{ invitation.target_name }}</strong>.<br>
          {{ t('invitationModal.selectFromProfileMenu') }}
        </p>
        <p v-else>
          {{ t('invitationModal.joinedOrg') }} <strong>{{ invitation.target_name }}</strong>.
        </p>
        <div class="inv-actions">
          <AeButton @click="$emit('done', { newProfile: newProfileCreated })">{{ t('invitationModal.close') }}</AeButton>
        </div>
      </template>

    </div>
  </AeModal>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AeModal, AeButton } from '@aevue/components'
import { useOrgStore } from '../stores/orgStore'
import { useAppStore } from '../stores/app'

const { t } = useI18n()
const props = defineProps({
  invitation: { type: Object, default: null }
})
const emit = defineEmits(['close', 'done'])

const orgStore = useOrgStore()
const appStore = useAppStore()

const step = ref('details')
const loading = ref(false)
const error = ref(null)
const newProfileCreated = ref(false)

watch(() => props.invitation, () => {
  step.value = 'details'
  error.value = null
  newProfileCreated.value = false
})

async function handleAccept() {
  if (props.invitation.invitation_type !== 'org') {
    await doAccept()
    return
  }
  // Per inviti org: se l'utente ha già un'org mostra avviso nuovo profilo
  if (appStore.user?.id_org) {
    step.value = 'new-profile'
  } else {
    await doAccept()
  }
}

async function confirmNewProfile() {
  await doAccept()
}

async function doAccept() {
  loading.value = true
  error.value = null
  const res = await orgStore.acceptInvitation(props.invitation.id_invitation)
  loading.value = false
  if (!res.ok) {
    error.value = res.message || t('invitationModal.acceptError')
    step.value = 'details'
    return
  }
  newProfileCreated.value = res.new_profile === true
  step.value = 'done'
}

async function handleReject() {
  loading.value = true
  await orgStore.rejectInvitation(props.invitation.id_invitation)
  loading.value = false
  emit('close')
}
</script>

<style scoped>
.inv-modal {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 300px;
}

.inv-org {
  font-size: 1.2rem;
  padding: 0.75rem 1rem;
  background: var(--ae-bg-secondary, #f5f5f5);
  border-radius: 8px;
  text-align: center;
}

.inv-from {
  color: var(--ae-text-secondary, #666);
  font-size: 0.9rem;
  margin: 0;
}

.inv-desc {
  margin: 0;
  font-size: 0.9rem;
}

.inv-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
}

.inv-new-profile-icon,
.inv-done-icon {
  font-size: 2rem;
  text-align: center;
}

.inv-hint {
  font-size: 0.85rem;
  color: var(--ae-text-secondary, #666);
  margin: 0;
}

.inv-error {
  color: var(--ae-danger, #e53e3e);
  font-size: 0.9rem;
}
</style>
